import React, { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Table({ columns, data, onRowClick, emptyText = 'Нет данных', pageSize = 10, loading }) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = [...(data || [])].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey], bv = b[sortKey];
    if (av === bv) return 0;
    const res = av > bv ? 1 : -1;
    return sortDir === 'asc' ? res : -res;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paged = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  };

  return (
    <div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {columns.map(col => (
                <th key={col.key} onClick={() => col.sortable !== false && handleSort(col.key)}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: '#64748B',
                    fontSize: 11,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    cursor: col.sortable !== false ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    borderBottom: '1px solid #E2E8F0',
                    position: 'sticky',
                    top: 0,
                    background: '#F8FAFC',
                    zIndex: 1,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {col.label}
                    {col.sortable !== false && (
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 0, marginLeft: 2 }}>
                        <ChevronUp size={9} style={{ opacity: sortKey === col.key && sortDir === 'asc' ? 1 : 0.25, color: sortKey === col.key && sortDir === 'asc' ? '#009995' : '#94A3B8' }} />
                        <ChevronDown size={9} style={{ opacity: sortKey === col.key && sortDir === 'desc' ? 1 : 0.25, marginTop: -3, color: sortKey === col.key && sortDir === 'desc' ? '#009995' : '#94A3B8' }} />
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {columns.map(c => (
                    <td key={c.key} style={{ padding: '14px 16px', borderBottom: '1px solid #F1F5F9' }}>
                      <div style={{
                        height: 14,
                        background: 'linear-gradient(90deg, #F1F5F9 25%, #E2E8F0 50%, #F1F5F9 75%)',
                        backgroundSize: '200% 100%',
                        borderRadius: 6,
                        width: `${50 + Math.random() * 40}%`,
                        animation: 'shimmer 1.5s infinite',
                      }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '48px 16px', textAlign: 'center', color: '#94A3B8' }}>
                  <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.6 }}>📋</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{emptyText}</div>
                </td>
              </tr>
            ) : (
              paged.map((row, i) => (
                <tr key={row.id || i}
                  onClick={() => onRowClick && onRowClick(row)}
                  style={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    transition: 'background 0.15s ease',
                    borderBottom: '1px solid #F1F5F9',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#F8FAFB'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  {columns.map(col => (
                    <td key={col.key} style={{
                      padding: '13px 16px',
                      color: '#334155',
                      verticalAlign: 'middle',
                    }}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderTop: '1px solid #F1F5F9',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} из {sorted.length}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{
                padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: 8,
                background: '#fff', cursor: page === 0 ? 'not-allowed' : 'pointer',
                opacity: page === 0 ? 0.4 : 1, display: 'flex', alignItems: 'center',
                transition: 'all 0.15s',
              }}>
              <ChevronLeft size={14} color="#64748B" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  style={{
                    padding: '6px 11px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: p === page ? 'none' : '1px solid #E2E8F0',
                    background: p === page ? 'linear-gradient(135deg, #00CEEE, #009995)' : '#fff',
                    color: p === page ? '#fff' : '#475569',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: p === page ? '0 2px 6px rgba(0,206,238,0.3)' : 'none',
                  }}>
                  {p + 1}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              style={{
                padding: '6px 10px', border: '1px solid #E2E8F0', borderRadius: 8,
                background: '#fff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                opacity: page >= totalPages - 1 ? 0.4 : 1, display: 'flex', alignItems: 'center',
                transition: 'all 0.15s',
              }}>
              <ChevronRight size={14} color="#64748B" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
